/* eslint-disable camelcase */
/* eslint-disable no-restricted-syntax */
import PropTypes from "prop-types";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { ModalBackground, ModalContainer } from "../components/SessionExpires";
import { fetchAccess, postLogin, resetTokenStatus } from "../services/authServices";
import { handleErrors } from "../services/handleErrors";
import { patchProfile } from "../services/profileServices";
import { ButtonPrimary, ButtonSecondary } from "../ui/buttons";
// Acá es donde estan los times de refresco
const RefreshTokenTime = {
  INACTIVITY_MODAL: process.env.TOKEN_LIFETIME || 27, // 27 minutos
  INACTIVITY_CLOSE_MODAL: process.env.TOKEN_MODAL_LIFETIME || 3, // 3 minutos
};
const ATTEMPTS_LOCK_FAIL_MFA = 5;
const detectNumber = (message) => parseInt(message.match(/\d+/g)[0], 10) >= ATTEMPTS_LOCK_FAIL_MFA;

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export const PUBLIC_PATHS = ["/login", "/resetPassword", "/signup"]; // Define public paths

export function AuthProvider({ children }) {
  const queryParams = new URLSearchParams(window.location.search);
  const [linkToken] = useState(queryParams.get("t"));

  const [currentUser, setCurrentUser] = useState(JSON.parse(localStorage.getItem("authUser")));
  const [userAccess, setUserAccess] = useState(null);
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const [countdown, setCountdown] = useState(RefreshTokenTime.INACTIVITY_CLOSE_MODAL);
  const modalTimeoutRef = useRef(null);

  const location = useLocation();

  function resetCountdown() {
    setShowInactivityModal(false);
    setCountdown(RefreshTokenTime.INACTIVITY_CLOSE_MODAL);
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
    }
  }

  function logout() {
    localStorage.removeItem("authUser");
    localStorage.removeItem("tokenTimestamp");
    setCurrentUser(null);
    setUserAccess(null);
    resetCountdown();
  }
  // eslint-disable-next-line consistent-return
  async function login(email, password, twofa, recordarDispositivo, browserHash, backupCode) {
    setUserAccess(null);
    try {
      const Login = await postLogin({ email, password, twofa, browserHash, backupCode });
      const { result, message } = Login;

      if (!result && !message) throw Login;
      if (result === 401 || result === 419) {
        logout();
        if (location.pathname === "/login") return result;
        return message;
      }
      if (result === 423 || result === 429) {
        handleErrors({ message, isSoftError: true }, toast.error);
      }
      if (result === 428) {
        handleErrors({ message, isSoftError: true }, detectNumber(message) ? toast.success : toast.error);
      }
      if (result > 400) {
        return result;
      }
      if (recordarDispositivo) {
        // solo guarda el browser hash en localstorage cuando se activa "recordar dispositivo"
        localStorage.setItem("authUser", JSON.stringify({ token: result.token, user: result.user }));
        localStorage.setItem("browserHash", JSON.stringify(result.browser_hash));
      } else {
        localStorage.setItem("authUser", JSON.stringify({ token: result.token, user: result.user }));
      }

      localStorage.setItem("tokenTimestamp", Date.now());
      resetTokenStatus();
      setCurrentUser({ token: result.token, user: result.user });
      return message;
    } catch (error) {
      localStorage.removeItem("authUser");
      setCurrentUser(null);
      throw error;
    }
  }

  async function refreshToken() {
    try {
      const user = JSON.parse(localStorage.getItem("authUser"));
      if (user.token) {
        const newToken = await fetchAccess("token")
          .then((response) => {
            const { result } = response;
            if (result > 400) {
              logout();
              throw response;
            }
            return result.token;
          })
          .catch((err) => {
            throw err;
          });
        user.token = newToken;
        localStorage.setItem("authUser", JSON.stringify(user));
        localStorage.setItem("tokenTimestamp", Date.now());
        setShowInactivityModal(false);
        resetTokenStatus();
        return;
      }
    } catch (err) {
      handleErrors(err, "Refresco de sesión fallido", logout);
    }
    logout();
    // eslint-disable-next-line consistent-return
    return true;
  }

  function getToken() {
    try {
      return JSON.parse(localStorage.getItem("authUser")).token;
    } catch (error) {
      return null;
    }
  }

  function getUserSucursalesCasasCentralesAsignadas() {
    const casasCentralesAsignadas = currentUser.user.casasCentralesAsignadas || [];
    const sucursalesAsignadas = currentUser.user.sucursalesAsignadas || [];
    return casasCentralesAsignadas.concat(sucursalesAsignadas);
  }

  function haveSucursalCasaCentralAsignada(sucursalCasaCentraItemId) {
    return getUserSucursalesCasasCentralesAsignadas().some((item) => item.itemId === sucursalCasaCentraItemId);
  }

  async function updateUserData({ telefono, oldPassword, newPassword }) {
    const result = await patchProfile({ telefono, oldPassword, newPassword });
    if (oldPassword && newPassword) {
      logout();
    } else {
      currentUser.user = result.result;
      localStorage.setItem("authUser", JSON.stringify(currentUser));
      setCurrentUser({ ...currentUser });
    }
    toast.success(result.message);
    return result.result;
  }

  async function getUserAccess() {
    if (linkToken) {
      return null;
    }

    if (userAccess !== null) {
      return userAccess;
    }

    const access = await fetchAccess().catch((e) => e);
    if (!access || access.error) {
      logout();
      // eslint-disable-next-line no-console
      return false;
    }
    setUserAccess(access);
    return access;
  }

  async function getFirstMenuPath() {
    const userAccessLocal = await getUserAccess();

    if (userAccessLocal && userAccessLocal.result) {
      for (const access of userAccessLocal.result) {
        if (access.menu.items) {
          for (const item of access.menu.items) {
            if (item.path) {
              return item.path;
            }
          }
        }
        return access.menu.path;
      }
    } else return "/login";

    return "/profile";
  }

  function getPermiso(moduloId) {
    const permisos = userAccess;
    if (userAccess) {
      return permisos.result.find((permiso) => permiso.id === moduloId);
    }
    getUserAccess();
    return null;
  }

  function haveAdminAccess(moduloId) {
    const permiso = getPermiso(moduloId);
    return permiso && permiso.permisoCat.text.toLowerCase() === "admin";
  }

  function haveAuditAccess(moduloId) {
    const permiso = getPermiso(moduloId);
    return permiso && permiso.permisoCat.text.toLowerCase() === "auditor";
  }

  function haveGeneralAccess(moduloId) {
    const permiso = getPermiso(moduloId);
    return permiso && permiso.permisoCat.text.toLowerCase() === "general";
  }

  function haveSucursalesAccess(moduloId) {
    const permiso = getPermiso(moduloId);
    return !permiso || permiso.filtroCat.text.toLowerCase() === "si";
  }

  function getMetadata(moduloId, attribute) {
    const permiso = getPermiso(moduloId);
    const metadata = permiso.metadata ? JSON.parse(permiso.metadata.replace(/<[^>]*>/g, "")) : {};
    if (attribute) {
      return metadata[attribute];
    }
    return metadata;
  }

  const handleModalExtend = useCallback(async () => {
    await refreshToken();
    setCountdown(RefreshTokenTime.INACTIVITY_CLOSE_MODAL);
  }, []);

  const handleModalClose = useCallback(() => {
    setCountdown(RefreshTokenTime.INACTIVITY_CLOSE_MODAL);
    setShowInactivityModal(false);
    logout();
  }, []);

  const value = {
    currentUser: currentUser && {
      ...currentUser,
      getPermiso,
      haveAdminAccess,
      haveAuditAccess,
      haveGeneralAccess,
      haveSucursalesAccess,
      getMetadata,
    },
    login,
    logout,
    getToken,
    refreshToken,
    updateUserData,
    getUserAccess,
    getFirstMenuPath,
    getPermiso,
    haveAdminAccess,
    haveAuditAccess,
    haveGeneralAccess,
    haveSucursalesAccess,
    haveSucursalCasaCentralAsignada,
    linkToken,
  };

  const getTokenTimestamp = () => localStorage.getItem("tokenTimestamp");

  const checkTokenExpiration = useCallback(() => {
    if (PUBLIC_PATHS.includes(location.pathname)) {
      return; // Do not calculate anything if on a public path
    }
    const tokenTimestamp = getTokenTimestamp();
    if (!tokenTimestamp) {
      logout();
      return;
    }

    const now = Date.now();
    const elapsedMinutes = (now - parseInt(tokenTimestamp, 10)) / (60 * 1000);
    const tokenLifetime = RefreshTokenTime.INACTIVITY_MODAL;
    const remainingMinutes = tokenLifetime - elapsedMinutes;

    if (remainingMinutes <= 0) {
      logout();
    } else if (remainingMinutes <= RefreshTokenTime.INACTIVITY_CLOSE_MODAL) {
      if (!showInactivityModal) {
        setShowInactivityModal(true);
        setCountdown(Math.floor(remainingMinutes * 60)); // Remaining seconds
      }
    } else {
      if (modalTimeoutRef.current) {
        clearTimeout(modalTimeoutRef.current);
      }
      // Schedule modal before expiration
      const timeToModal = (remainingMinutes - RefreshTokenTime.INACTIVITY_CLOSE_MODAL) * 60 * 1000;

      // Ensure modal shows only when enough time is left
      if (timeToModal > 0) {
        modalTimeoutRef.current = setTimeout(() => {
          if (Math.abs(getTokenTimestamp() - tokenTimestamp) < 1000) {
            setShowInactivityModal(true);
            setCountdown(RefreshTokenTime.INACTIVITY_CLOSE_MODAL * 60);
          }
        }, timeToModal);
      }
    }
  }, [logout, showInactivityModal, location.pathname]);

  useEffect(() => {
    if (PUBLIC_PATHS.includes(location.pathname)) {
      resetCountdown();
      return;
    }

    checkTokenExpiration();
    // eslint-disable-next-line consistent-return
  }, [location.pathname, checkTokenExpiration]);

  useEffect(() => {
    if (PUBLIC_PATHS.includes(location.pathname)) {
      resetCountdown();
      return;
    }

    let countdownInterval;

    if (showInactivityModal) {
      countdownInterval = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            clearInterval(countdownInterval);
            logout();
            setShowInactivityModal(false);
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    }
    if (countdown === 0) {
      handleModalClose();
    }
    // eslint-disable-next-line consistent-return
    return () => clearInterval(countdownInterval);
  }, [countdown, handleModalClose, showInactivityModal, location.pathname]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {!PUBLIC_PATHS.includes(location.pathname) && showInactivityModal && (
        <ModalBackground>
          <ModalContainer>
            <h2 style={{ textAlign: "center", fontSize: "24px", fontWeight: "bold" }}>Notamos que no estás presente</h2>
            <p style={{ textAlign: "center", fontSize: "16px" }}>
              Tu sesión se cerrará en:{" "}
              <strong style={{ fontSize: "18px" }}>
                {Math.floor(countdown / 60)}:{`0${countdown % 60}`.slice(-2)}
              </strong>
            </p>
            <div className="d-flex justify-content-end align-items-center">
              <ButtonPrimary onClick={handleModalExtend}>Extender Sesión</ButtonPrimary>
              <ButtonSecondary className="mr-0" onClick={handleModalClose}>
                Salir
              </ButtonSecondary>
            </div>
          </ModalContainer>
        </ModalBackground>
      )}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
